<!-- src/components/InstantWinList.svelte -->
<script>
	import { writable } from 'svelte/store';

	/**
	 * PROPS:
	 *   instantWins: array of objects, each possibly having:
	 *     {
	 *       number: Number,
	 *       prize: String,
	 *       claimedBy: String|null,
	 *       imageUrl: String|null   // (for new approach)
	 *     }
	 */
	export let instantWins = [];

	// Old approach constants
	const FIREBASE_STORAGE_BASE_URL =
		'https://firebasestorage.googleapis.com/v0/b/staging-ac5f0.appspot.com/o/iwimages%2F';
	const FIREBASE_STORAGE_TOKEN = 'your-token'; // If you need an auth token, put it here
	const FALLBACK_IMAGE_URL = 'https://www.hotdraw.co.uk/hd-full-trans.png';

	/**
	 * 1) convertPrizeNameToImageName:
	 *   - Lowercases
	 *   - Removes unwanted chars (but keeps & and digits)
	 *   - Replaces spaces with hyphens
	 *   - Leaves & intact if present
	 */
	
	function convertPrizeNameToImageName(prizeName) {
		return prizeName
			.toLowerCase()
			.replace(/&/g, 'and') // crucial to avoid leftover double dash
			.replace(/£/g, '')
			.replace(/[',:;()\[\]{}!^*$@+={}\\|?]/g, '')
			.trim()
			.replace(/\s+/g, '-')
			.replace(/[^a-z0-9-]/g, '');
	}

	/**
	 * 2) getImageUrl:
	 *   - Guesses a possible extension (.png, .jpg, .jpeg)
	 *   - Immediately returns the first guess
	 */
	function getImageUrl(prizeName) {
		const imageNameBase = convertPrizeNameToImageName(prizeName);
		const possibleExtensions = ['png', 'jpg', 'jpeg'];

		for (let ext of possibleExtensions) {
			const imageName = `${imageNameBase}.${ext}`;
			const encodedImageName = encodeURIComponent(imageName);
			// Append &token=FIREBASE_STORAGE_TOKEN if you need it publicly
			const url = `${FIREBASE_STORAGE_BASE_URL}${encodedImageName}?alt=media`;
			return url; // we return the first constructed URL
		}

		// If we somehow have no extension, fallback
		return FALLBACK_IMAGE_URL;
	}

	/**
	 * 3) combinedPrizeImages:
	 *   - If we do not have a known Firestore URL, we guess the path
	 */
	function combinedPrizeImages(prizeName) {
		const url = getImageUrl(prizeName);
		return url || FALLBACK_IMAGE_URL;
	}

	// We'll group by prize => array of [prizeName, dataObject]
	const sortedGroupedPrizes = writable([]);

	// Accordion open/closed state
	const openAccordions = writable({});

	/**
	 * groupAndSortInstantWins:
	 *   - Groups by prize
	 *   - Aggregates total, claimed, unclaimed
	 *   - If any item has a `.imageUrl`, store it in `data.imageUrl` (so we skip guessing)
	 */
	function groupAndSortInstantWins() {
		const groups = {};

		instantWins.forEach((win) => {
			if (!groups[win.prize]) {
				groups[win.prize] = {
					total: 0,
					claimed: 0,
					unclaimed: 0,
					entries: [],
					imageUrl: null
				};
			}

			groups[win.prize].total++;
			if (win.claimedBy) {
				groups[win.prize].claimed++;
			} else {
				groups[win.prize].unclaimed++;
			}

			groups[win.prize].entries.push(win);

			// If this win has an imageUrl, use it for the group if not set
			if (win.imageUrl && !groups[win.prize].imageUrl) {
				groups[win.prize].imageUrl = win.imageUrl;
			}
		});

		// Convert groups to an array for sorting
		const groupedArray = Object.entries(groups);
		// Example: sort ascending by total
		groupedArray.sort((a, b) => a[1].total - b[1].total);

		sortedGroupedPrizes.set(groupedArray);
	}

	// Toggle accordion
	function toggleAccordion(prize) {
		openAccordions.update((current) => {
			return { ...current, [prize]: !current[prize] };
		});
	}

	// Whenever `instantWins` changes, regroup
	$: groupAndSortInstantWins();
</script>

<div class="instant-win-prizes">
	<h2 class="title">Instant Win Prizes</h2>

	{#if $sortedGroupedPrizes && $sortedGroupedPrizes.length > 0}
		<div>
			{#each $sortedGroupedPrizes as [prize, data]}
				<div class="accordion">
					<!-- Accordion Header -->
					<button
						type="button"
						class="accordion-header"
						on:click={() => toggleAccordion(prize)}
						aria-expanded={$openAccordions[prize]}
						aria-controls="content-{prize.replace(/\s+/g, '-').toLowerCase()}"
					>
						<div style="display: flex; align-items: center;">
							<!-- If Firestore imageUrl is present, use that;
							     otherwise, guess the path. -->
							<img
								src={data.imageUrl ? data.imageUrl : combinedPrizeImages(prize)}
								alt={prize}
								class="prize-image"
								on:error={(e) => (e.target.src = FALLBACK_IMAGE_URL)}
							/>
							<span class="accordion-title">{prize}</span>
						</div>
						<div class="accordion-toggle">
							<div class="accordion-meta">
								{data.claimed} of {data.total} Won
							</div>
							<svg
								class={`accordion-icon ${$openAccordions[prize] ? 'open' : ''}`}
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M19 9l-7 7-7-7"
								/>
							</svg>
						</div>
					</button>

					<!-- Accordion Content -->
					{#if $openAccordions[prize]}
						<div
							class="accordion-content"
							id="content-{prize.replace(/\s+/g, '-').toLowerCase()}"
							role="region"
						>
							{#if data.entries.length > 0}
								<table class="instant-win-table">
									<thead>
										<tr>
											<th>Ticket Number</th>
											<th>Winner</th>
										</tr>
									</thead>
									<tbody>
										{#each data.entries as win}
											<tr>
												<td>{win.number}</td>
												<td>{win.claimedBy ? win.claimedBy : '-'}</td>
											</tr>
										{/each}
									</tbody>
								</table>
							{:else}
								<p class="no-instant-wins">No instant win prizes assigned.</p>
							{/if}
						</div>
					{/if}
				</div>
			{/each}
		</div>
	{:else}
		<p class="no-instant-wins">No instant win prizes available.</p>
	{/if}
</div>

<style>
	.title {
		font-size: 1.5rem;
		font-weight: bold;
		margin-bottom: 1.5rem;
	}

	.accordion {
		border: 1px solid #ccc;
		border-radius: 4px;
		margin-bottom: 0.5rem;
		overflow: hidden;
	}

	.accordion-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 0.45rem 0.75rem;
		background-color: #f7f7f7;
		cursor: pointer;
		transition: background-color 0.3s;
		appearance: none;
		border: none;
		width: 100%;
		outline: none;
		color: #222;
	}

	.accordion-toggle {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
	}

	.accordion-header:hover {
		background-color: #eaeaea;
	}

	.accordion-title {
		font-size: 1rem;
		font-weight: 600;
		text-align: left;
	}

	.accordion-meta {
		font-size: 0.9rem;
		color: #555;
	}

	.accordion-icon {
		width: 1rem;
		height: 1rem;
		transition: transform 0.3s;
	}

	.accordion-icon.open {
		transform: rotate(180deg);
	}

	.accordion-content {
		padding: 0.75rem 1rem;
		background-color: #fff;
	}

	.instant-win-table {
		width: 100%;
		border-collapse: collapse;
		font-size: 0.9rem;
	}

	.instant-win-table th,
	.instant-win-table td {
		border: 1px solid #ddd;
		padding: 0.5rem;
		text-align: left;
	}

	.instant-win-table th {
		background-color: #f0f0f0;
		font-weight: bold;
	}

	.instant-win-table tbody tr:nth-child(even) {
		background-color: #f9f9f9;
	}

	.no-instant-wins {
		font-size: 0.9rem;
		color: #777;
	}

	.prize-image {
		width: 50px;
		height: 50px;
		object-fit: contain;
		margin-right: 0.5rem;
	}

	@media (max-width: 600px) {
		.prize-image {
			width: 40px;
			height: 40px;
		}
	}

	.instant-win-prizes {
		max-height: 420px;
		overflow-y: auto;
		width: 100%;
	}

	
</style>
